import React from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useAdminDashboard from "./hooks/useAdminDashboard";

import DashboardHero from "./components/DashboardHero";
import DashboardStatCard from "./components/DashboardStatCard";
import DistributionPieCard from "./components/DistributionPieCard";
import MessagesPreviewCard from "./components/MessagesPreviewCard";
import NotificationsPreviewCard from "./components/NotificationsPreviewCard";
import RecentAdherentsGrid from "./components/RecentAdherentsGrid";
import VisitsChartCard from "./components/VisitsChartCard";
import useScrollToTop from "../../../../hooks/useScrollToTop";

import {
  buildGenderChartData,
  buildGroupChartData,
  getDashboardStats,
} from "./utils/dashboardUtils";

import {
  FaBell,
  FaEnvelope,
  FaEye,
  FaUsers,
  FaUserFriends,
} from "react-icons/fa";
import { GiKimono } from "react-icons/gi";

export default function Dashboard() {
  useScrollToTop();
  const dashboard = useAdminDashboard();

  const {
    loading,
    refreshing,
    clubInfo,
    userInfo,
    adherents,
    recentAdherents,
    groupDistribution,
    genderDistribution,
    groupColorMap,
    visits,
    messages,
    notifications,
    unreadMessages,
    unreadNotifications,
    deletingVisits,
    actions,
  } = dashboard;

  const stats = getDashboardStats({
    adherents,
    groupDistribution,
    genderDistribution,
    visits,
    messages,
    notifications,
  });

  const groupChartData = buildGroupChartData(groupDistribution, groupColorMap);
  const genderChartData = buildGenderChartData(genderDistribution);

  return (
    <div className="space-y-6">
      <DashboardHero
        loading={loading}
        refreshing={refreshing}
        clubInfo={clubInfo}
        userInfo={userInfo}
        stats={stats}
        onRefresh={actions.refreshDashboard}
      />

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <DashboardStatCard
          icon={FaUsers}
          label="Adhérents"
          value={stats.totalAdherents}
          helper="Membres enregistrés"
          tone="blue"
          loading={loading}
        />

        <DashboardStatCard
          icon={GiKimono}
          label="Groupes"
          value={stats.totalGroups}
          helper="Groupes actifs"
          tone="orange"
          loading={loading}
        />

        <DashboardStatCard
          icon={FaEye}
          label="Visites"
          value={stats.totalVisits}
          helper="Visites suivies"
          tone="emerald"
          loading={loading}
        />

        <DashboardStatCard
          icon={FaEnvelope}
          label="Messages"
          value={unreadMessages}
          helper="Non lus"
          tone="rose"
          loading={loading}
        />

        <DashboardStatCard
          icon={FaBell}
          label="Notifications"
          value={unreadNotifications}
          helper="À traiter"
          tone="violet"
          loading={loading}
        />
      </section>

      <section className="grid items-start gap-6 xl:grid-cols-[minmax(0,1.6fr)_minmax(340px,0.8fr)]">
        <div className="space-y-6">
          <RecentAdherentsGrid
            loading={loading}
            adherents={recentAdherents}
            groupColorMap={groupColorMap}
          />

          <VisitsChartCard
            loading={loading}
            visits={visits}
            deleting={deletingVisits}
            onDeleteVisits={actions.deleteVisitsRange}
          />

          <div className="grid gap-6 2xl:grid-cols-2">
            <DistributionPieCard
              loading={loading}
              title="Répartition par groupe"
              subtitle="Visualisez rapidement les effectifs par cours ou groupe."
              icon={FaUserFriends}
              data={groupChartData}
              emptyLabel="Aucune donnée de groupe disponible."
            />

            <DistributionPieCard
              loading={loading}
              title="Répartition par genre"
              subtitle="Suivi indicatif des adhérents par genre."
              icon={FaUsers}
              data={genderChartData}
              emptyLabel="Aucune donnée de genre disponible."
            />
          </div>
        </div>

        <aside className="space-y-6">
          <MessagesPreviewCard
            loading={loading}
            messages={messages}
            unreadCount={unreadMessages}
            onToggleRead={actions.toggleMessageRead}
            onDelete={actions.deleteMessage}
          />

          <NotificationsPreviewCard
            loading={loading}
            notifications={notifications}
            unreadCount={unreadNotifications}
            onToggleRead={actions.toggleNotificationRead}
            onDelete={actions.deleteNotification}
          />
        </aside>
      </section>

      <ToastContainer position="bottom-right" theme="colored" />
    </div>
  );
}